// src/pages/components/Checklist.tsx
import React, { useState } from 'react';
import { tasksApi, TaskChecklistItem } from '../api/tasks';

interface ChecklistProps {
  taskId: string;
  items: TaskChecklistItem[];
  onRefresh: () => void;
}

function Checklist({ taskId, items, onRefresh }: ChecklistProps) {
  const [newItem, setNewItem] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItem.trim()) return;

    setAdding(true);
    try {
      await tasksApi.addChecklistItem(taskId, { item: newItem });
      setNewItem('');
      onRefresh();
    } catch (err: any) {
      alert(err.message || 'Failed to add checklist item');
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (itemId: string, isDone: boolean) => {
    try {
      await tasksApi.updateChecklistItem(itemId, { is_done: !isDone });
      onRefresh();
    } catch (err: any) {
      alert(err.message || 'Failed to update checklist item');
    }
  };

  const handleDelete = async (itemId: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await tasksApi.deleteChecklistItem(itemId);
        onRefresh();
      } catch (err: any) {
        alert(err.message || 'Failed to delete checklist item');
      }
    }
  };

  const sortedItems = [...items].sort((a, b) => a.order_index - b.order_index);
  const completedCount = items.filter(i => i.is_done).length;

  return (
    <div className="bg-white rounded-xl shadow-md p-5 mb-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Checklist</h3>
          {items.length > 0 && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
              {completedCount}/{items.length} completed
            </span>
          )}
        </div>
      </div>
      
      <form onSubmit={handleAdd} className="flex gap-2 mb-4">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder="Add a new item..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
        />
        <button 
          type="submit" 
          disabled={adding || !newItem.trim()}
          className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 font-medium"
        >
          {adding ? 'Adding...' : 'Add Item'}
        </button>
      </form>

      <div className="space-y-2 max-h-80 overflow-y-auto">
        {sortedItems.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-sm">No checklist items</p>
            <p className="text-xs">Add items to track subtasks</p>
          </div>
        ) : (
          sortedItems.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors group">
              <label className="flex items-center gap-3 flex-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={item.is_done}
                  onChange={() => handleToggle(item.id, item.is_done)}
                  className="w-5 h-5 text-green-600 rounded border-gray-300 focus:ring-green-500 cursor-pointer"
                />
                <span className={`text-gray-700 ${item.is_done ? 'line-through text-gray-400' : ''}`}>
                  {item.item}
                </span>
              </label>
              <button 
                onClick={() => handleDelete(item.id)}
                className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-700 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>

      {items.length > 0 && completedCount === items.length && items.length > 0 && (
        <div className="mt-3 p-2 bg-green-50 rounded-lg text-center">
          <span className="text-sm text-green-700">🎉 All tasks completed!</span>
        </div>
      )}
    </div>
  );
}

export default Checklist;